/**
 * The small pieces every screen is built from.
 *
 * Two rules run through all of them: a touch target is never smaller than
 * 44x44, and no piece of information is carried by colour alone -- there is
 * always a word, a symbol or a number saying the same thing.
 */
import {
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { formatMoney, type PaymentSource, type TransactionType } from '@wis/shared';

/* ---------- money ---------- */

export interface MoneyProps {
  sen: number;
  size?: 'display' | 'large' | 'medium' | 'inline';
  currency?: string;
  signed?: boolean;
  /** Renders in the "not spending" tone for transfers and income. */
  excluded?: boolean;
  className?: string;
}

export function Money({
  sen,
  size = 'inline',
  currency = 'MYR',
  signed = false,
  excluded = false,
  className,
}: MoneyProps): JSX.Element {
  const sizeClass = size === 'inline' ? '' : ` money--${size}`;
  const toneClass = excluded ? ' money--excluded' : sen < 0 ? ' money--negative' : '';
  return (
    <span className={`money${sizeClass}${toneClass}${className ? ` ${className}` : ''}`}>
      {formatMoney(sen, { currency, signed })}
    </span>
  );
}

/* ---------- badges ---------- */

const SOURCE_LABELS: Record<PaymentSource, { label: string; title: string }> = {
  manual: { label: 'Typed', title: 'Entered by hand' },
  'apple-pay-shortcut': {
    label: 'Shortcut',
    title: 'Captured by an Apple Wallet Shortcut you triggered',
  },
  'maybank-quick-capture': {
    label: 'MAE ask',
    title: 'Captured by the Shortcut that asks after you close MAE',
  },
  'tng-quick-capture': {
    label: 'TNG ask',
    title: 'Captured by the Shortcut that asks after you close TNG eWallet',
  },
  'maybank-receipt': { label: 'MAE receipt', title: 'Read from a MAE or Maybank receipt' },
  'tng-receipt': { label: 'TNG receipt', title: "Read from a Touch 'n Go receipt" },
  'csv-import': { label: 'CSV', title: 'Imported from a CSV file' },
  'json-import': { label: 'Backup', title: 'Restored from a backup file' },
};

export function SourceBadge({ source }: { source: PaymentSource }): JSX.Element {
  const entry = SOURCE_LABELS[source] ?? SOURCE_LABELS.manual;
  return (
    <span className="badge" title={entry.title}>
      {entry.label}
    </span>
  );
}

export function sourceLabel(source: PaymentSource): string {
  return (SOURCE_LABELS[source] ?? SOURCE_LABELS.manual).label;
}

const TYPE_LABELS: Record<TransactionType, string> = {
  expense: 'Expense',
  income: 'Income',
  refund: 'Refund',
  transfer: 'Transfer',
};

export function TypeBadge({ type }: { type: TransactionType }): JSX.Element | null {
  if (type === 'expense') return null;
  return (
    <span className="badge badge--type" title={`${TYPE_LABELS[type]} - not counted as spending`}>
      {TYPE_LABELS[type]}
    </span>
  );
}

export function typeLabel(type: TransactionType): string {
  return TYPE_LABELS[type];
}

export function SyncBadge({ state }: { state: 'local' | 'pending' | 'synced' | 'failed' }): JSX.Element | null {
  if (state === 'synced' || state === 'local') return null;
  const label = state === 'pending' ? 'Queued' : 'Sync failed';
  return (
    <span className={`badge badge--${state}`} title={label}>
      {label}
    </span>
  );
}

/* ---------- buttons ---------- */

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'accent' | 'secondary' | 'quiet' | 'danger';
  block?: boolean;
  large?: boolean;
}

export function Button({
  variant = 'secondary',
  block = false,
  large = false,
  className,
  type = 'button',
  ...rest
}: ButtonProps): JSX.Element {
  const classes = [
    'button',
    `button--${variant}`,
    block ? 'button--block' : '',
    large ? 'button--large' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return <button type={type} className={classes} {...rest} />;
}

/* ---------- fields ---------- */

export interface FieldProps {
  label: string;
  hint?: string;
  error?: string;
  children: (props: { id: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean }) => ReactNode;
}

export function Field({ label, hint, error, children }: FieldProps): JSX.Element {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {children({
        id,
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
        ...(error ? { 'aria-invalid': true } : {}),
      })}
      {error ? (
        <p className="field__error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
      {hint ? (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>): JSX.Element {
  return <input {...props} className={`input ${props.className ?? ''}`.trim()} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>): JSX.Element {
  return <select {...props} className={`select ${props.className ?? ''}`.trim()} />;
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>): JSX.Element {
  return <textarea {...props} className={`textarea ${props.className ?? ''}`.trim()} />;
}

/* ---------- segmented control ---------- */

export interface SegmentedProps<T extends string> {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}

export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: SegmentedProps<T>): JSX.Element {
  return (
    <div className="segmented" role="radiogroup" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          className="segmented__option"
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/* ---------- switch ---------- */

export function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className="switch"
      onClick={() => onChange(!checked)}
    >
      <span className="switch__knob" />
    </button>
  );
}

/* ---------- states ---------- */

export function EmptyState({
  glyph,
  title,
  body,
  action,
}: {
  glyph: string;
  title: string;
  body: string;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className="empty-state">
      <div className="empty-state__glyph" aria-hidden="true">
        {glyph}
      </div>
      <p className="empty-state__title">{title}</p>
      <p className="empty-state__body">{body}</p>
      {action ? <div className="empty-state__action">{action}</div> : null}
    </div>
  );
}

export function LoadingRows({ count = 3 }: { count?: number }): JSX.Element {
  return (
    <div className="card card--flush" aria-busy="true" aria-live="polite">
      <span className="visually-hidden">Loading</span>
      {Array.from({ length: count }, (_unused, index) => (
        <div key={index} className="tx-row" aria-hidden="true">
          <div className="skeleton" style={{ width: '2.25rem', height: '2.25rem' }} />
          <div>
            <div className="skeleton" style={{ width: '60%', height: '0.9rem' }} />
            <div className="skeleton" style={{ width: '35%', height: '0.7rem', marginTop: 6 }} />
          </div>
          <div className="skeleton" style={{ width: '4rem', height: '1rem' }} />
        </div>
      ))}
    </div>
  );
}

export function Notice({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warning' | 'danger' | 'offline';
  children: ReactNode;
}): JSX.Element {
  return (
    <div className={`notice notice--${tone}`} role={tone === 'danger' ? 'alert' : 'status'}>
      <span>{children}</span>
    </div>
  );
}

/* ---------- sheet ---------- */

export interface SheetProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Action rendered at the top right, usually "Save". */
  action?: ReactNode;
}

/**
 * A bottom sheet, which is where a phone expects a secondary task to happen.
 * Focus moves in on open and back out on close, and Escape always dismisses.
 */
export function Sheet({ open, title, onClose, children, action }: SheetProps): JSX.Element | null {
  const panel = useRef<HTMLDivElement>(null);
  const returnFocus = useRef<Element | null>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return undefined;
    returnFocus.current = document.activeElement;
    // Focus the panel itself, not the first control inside it.
    //
    // Grabbing the first focusable element would land on "Close", which is
    // both the wrong place to start and actively harmful: the content of a
    // sheet often wants to focus its own field, and two components competing
    // for focus can interrupt text the user is part-way through typing.
    const timer = window.setTimeout(() => {
      panel.current?.focus();
    }, 30);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    // Stop the page behind the sheet from scrolling with it.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      (returnFocus.current as HTMLElement | null)?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className="sheet-backdrop" onClick={onClose} aria-hidden="true" />
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        ref={panel}
      >
        <div className="sheet__grabber" aria-hidden="true" />
        <div className="sheet__head">
          <h2 id={titleId} className="section__title">
            {title}
          </h2>
          <div className="row">
            {action}
            <Button variant="quiet" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
        {children}
      </div>
    </>
  );
}

/* ---------- confirmation ---------- */

export function ConfirmPanel({
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
  /** When set, the confirm button stays disabled until this exact word is typed. */
  requirePhrase,
  typed,
  onTypedChange,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  requirePhrase?: string;
  typed?: string;
  onTypedChange?: (value: string) => void;
}): JSX.Element {
  const ready = !requirePhrase || typed === requirePhrase;
  return (
    <div className="stack">
      <p className="small">{body}</p>
      {requirePhrase ? (
        <Field label={`Type ${requirePhrase} to confirm`}>
          {(props) => (
            <TextInput
              {...props}
              value={typed ?? ''}
              onChange={(event) => onTypedChange?.(event.target.value)}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
            />
          )}
        </Field>
      ) : null}
      <div className="row">
        <Button variant="quiet" onClick={onCancel} className="grow">
          Cancel
        </Button>
        <Button variant="danger" onClick={onConfirm} disabled={!ready} className="grow">
          {confirmLabel}
        </Button>
      </div>
      <p className="visually-hidden">{title}</p>
    </div>
  );
}
